import React, { useState } from "react";
import {
  Table,
  Button,
  Tag,
  Modal,
  message,
  Empty,
  Select,
  Dropdown,
  Input,
  Form,
  DatePicker,
  Descriptions,
} from "antd";
import {
  ReloadOutlined,
  DeleteOutlined,
  MoreOutlined,
  EyeOutlined,
  CalendarOutlined,
  SearchOutlined,
  EditOutlined,
  HistoryOutlined,
  PrinterOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { User, MapPin } from "lucide-react";
import { useDebounce } from "use-debounce";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useCart } from "../../Context";
import {
  useCustomersQuery,
  useActivePaymentStatusesQuery,
  useActiveOrderStatusesQuery,
  useOrdersQuery,
  useDeleteOrderMutation,
  useOrderByIdRequest,
  useActivePaymentMethodsQuery,
  useActiveBankAccountsQuery,
  useCreateCustomerCreditMutation,
} from "../../Services";
import { Order, Customer } from "../../Types";
import {
  PageHeader,
  InvoiceModal,
  tablePagination,
  PaymentOption,
  PaymentOptionValue,
} from "../../components";
import { OrderDetailsDrawer, OrderHistoryDrawer } from "./../../components";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY, ROUTE } from "../../Constant";
import { useSectionAccess } from "../../Services";
import { SalesOrderAction } from "../../Types";
import { CustomerCreditTransactionType, DiscountMode } from "../../Enums";
import { CreditPaymentDrawer } from "../../components";

const { Option } = Select;

export const SellingHistory: React.FC = () => {
  const navigate = useNavigate();
  const { setCart, setCustomer, setTable, setIsWholeSale, setEditingOrderId } =
    useCart();
  const { isAllowed } = useSectionAccess("sales_order");

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [paymentStatusId, setPaymentStatusId] = useState<string | null>(null);
  const [orderStatusId, setOrderStatusId] = useState<string | null>(null);
  const [sellingType, setSellingType] = useState<string | null>(null);

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

  // Credit Payment Modal states
  const [isCreditPaymentDrawerOpen, setIsCreditPaymentDrawerOpen] =
    useState(false);
  const [creditPaymentOrder, setCreditPaymentOrder] = useState<Order | null>(
    null,
  );
  const [creditPaymentForm] = Form.useForm();
  const [paymentMethodCode, setPaymentMethodCode] = useState<string>("");
  const [paymentDetails, setPaymentDetails] = useState<PaymentOptionValue>({
    amount: 0,
    note: "",
    bank_account_id: "",
    transaction_no: "",
    reference_no: "",
    cheques: [],
  });
  const [repayAmount, setRepayAmount] = useState<number>(0);

  const { data: bankAccounts = [] } = useActiveBankAccountsQuery();
  const { data: paymentMethods = [] } = useActivePaymentMethodsQuery();
  const createRepaymentMutation = useCreateCustomerCreditMutation();

  const handleOpenCreditPayment = (order: Order) => {
    setCreditPaymentOrder(order);
    setPaymentMethodCode("");
    setPaymentDetails({
      amount: 0,
      note: "",
      bank_account_id: "",
      transaction_no: "",
      reference_no: "",
      cheques: [],
    });
    setRepayAmount(0);
    creditPaymentForm.resetFields();
    setIsCreditPaymentDrawerOpen(true);
  };

  const handleSaveCreditPayment = async (values: any) => {
    try {
      if (!creditPaymentOrder) return;
      const pm = paymentMethods.find(
        (m: any) => m.id === values.payment_method_id,
      );
      if (!pm) {
        message.error("Invalid payment method.");
        return;
      }

      const amount = values.paymentDetails?.amount ?? 0;

      if (amount <= 0) {
        message.error("Payment amount must be greater than zero.");
        return;
      }

      if (amount > creditPaymentOrder.due_amount + 0.001) {
        message.error(
          `Payment amount exceeds the outstanding due of this order (LKR ${creditPaymentOrder.due_amount.toLocaleString()}).`,
        );
        return;
      }

      const res = await createRepaymentMutation.mutateAsync({
        customer_id: creditPaymentOrder.customer_id as any,
        order_id: creditPaymentOrder.id,
        transaction_type_id: CustomerCreditTransactionType.RECEIVED,
        amount,
        payment_method_code: pm.code,
        bank_account_id: values.paymentDetails?.bank_account_id || null,
        transaction_no: values.paymentDetails?.transaction_no || null,
        reference_no: values.paymentDetails?.reference_no || null,
        cheque_no: values.paymentDetails?.cheque_no || null,
        bank_name: values.paymentDetails?.bank_name || null,
        cheque_date: values.paymentDetails?.cheque_date || null,
        note: values.paymentDetails?.note || "",
        transaction_date: values.transaction_date
          ? values.transaction_date.format("YYYY-MM-DD HH:mm:ss")
          : null,
      });

      if (res?.success) {
        message.success(res.message || "Credit payment recorded successfully.");
        setIsCreditPaymentDrawerOpen(false);
        setCreditPaymentOrder(null);
        refetch();
      } else {
        message.error(res?.message || "Failed to record credit payment.");
      }
    } catch (err: any) {
      message.error(
        err?.message || "An error occurred while saving the credit payment.",
      );
    }
  };

  const orderByIdRequest = useOrderByIdRequest();
  const deleteMutation = useDeleteOrderMutation();

  const handleEditOrder = async (orderId: string) => {
    try {
      const res = await orderByIdRequest.mutateAsync(orderId);
      if (res) {
        const order: Order = res;

        const cartItems = (order.items || []).map((item: any) => {
          const discountAmount = item.discount_amount ?? 0;
          const totalListPrice = item.price * item.quantity;
          const qty = item.quantity || 1;
          let discountInput: number;
          let discountMode: DiscountMode = DiscountMode.AMOUNT;
          if (discountAmount > 0 && totalListPrice > 0) {
            const pct = (discountAmount / totalListPrice) * 100;
            if (Math.abs(pct - Math.round(pct)) < 0.01) {
              discountInput = Math.round(pct);
              discountMode = DiscountMode.PERCENT;
            } else {
              discountInput = discountAmount / qty;
            }
          } else {
            discountInput = 0;
          }
          return {
            id: item.product_id as any,
            name: item.product_name || "Unknown Product",
            price: item.price,
            sold_price: item.sold_price ?? item.price,
            image: "",
            batch_no: item.batch_no || "",
            qty: item.quantity,
            cost_price: item.cost_price,
            is_tempory_item: item.is_tempory_item,
            description: item.description || "",
            discount_amount: discountAmount,
            discount_input: discountInput,
            discount_mode: discountMode,
            rawProduct: {
              id: item.product_id as any,
              title: item.product_name || "",
              sku_code: item.sku_code || "",
              unit: item.unit || "",
              is_tempory_item: item.is_tempory_item,
              stocks: [
                {
                  id: item.stock_id,
                  batch_no: item.batch_no || "",
                  retail_price: item.is_wholesale_sale ? 0 : item.price,
                  wholesale_price: item.is_wholesale_sale ? item.price : 0,
                  cost_price: item.cost_price || 0,
                  quantity: item.quantity,
                },
              ],
            },
          };
        });

        setCart(cartItems);
        setCustomer(
          order.customer_id
            ? {
                id: order.customer_id as any,
                name: order.customer_name || "",
                credit_limit: order?.credit_limit,
              }
            : null,
        );
        setTable(
          order.table_id
            ? { id: order.table_id, name: order.table_name || "" }
            : null,
        );
        setIsWholeSale(order.selling_type === "wholesale");
        setEditingOrderId(order.id);

        message.success("Order items loaded to cart session.");
        navigate(`../${ROUTE.sales_billing}`);
      } else {
        message.error("Failed to load order details for editing.");
      }
    } catch (err: any) {
      message.error(
        err?.message || "An error occurred while loading order to cart.",
      );
    }
  };

  const { data: customerResponse } = useCustomersQuery();

  const customers = customerResponse?.data ?? [];
  const { data: paymentStatuses = [] } = useActivePaymentStatusesQuery();
  const { data: orderStatuses = [] } = useActiveOrderStatusesQuery();

  const {
    data: ordersList,
    isLoading,
    refetch,
  } = useOrdersQuery({
    page,
    limit: PAGE_SIZE,
    search,
    customer_id: customerId,
    payment_status_id: paymentStatusId,
    order_status_id: orderStatusId,
    selling_type: sellingType,
  });

  const totalOrders = ordersList?.total || 0;

  const handleOpenDetails = (id: string) => {
    setSelectedOrderId(id);
    setDetailsDrawerOpen(true);
  };

  const handleOpenHistoryDirect = (id: string) => {
    setSelectedOrderId(id);
    setHistoryDrawerOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDrawerOpen(false);
    setSelectedOrderId(null);
    setHistoryDrawerOpen(false);
  };

  const handleOpenHistory = () => setHistoryDrawerOpen(true);
  const handleCloseHistory = () => setHistoryDrawerOpen(false);

  const columns = [
    {
      title: "Date & Time",
      dataIndex: "order_date",
      key: "order_date",
      render: (v: string) => (
        <span className="text-xs text-gray-600 flex items-center gap-1.5 font-medium">
          <CalendarOutlined className="text-gray-400" />
          {dayjs(v).format("YYYY-MM-DD HH:mm")}
        </span>
      ),
    },
    {
      title: "Order No",
      dataIndex: "order_no",
      key: "order_no",
      render: (v: string) => (
        <span className="font-bold text-gray-800 text-xs">{v}</span>
      ),
    },
    {
      title: "Type",
      dataIndex: "selling_type",
      key: "selling_type",
      render: (t: string) => (
        <Tag
          color={t === "wholesale" ? "purple" : "orange"}
          className="capitalize text-[10px] font-bold"
        >
          {t}
        </Tag>
      ),
    },
    {
      title: "Customer",
      key: "customer_table",
      render: (_: any, rec: Order) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-semibold text-gray-700 text-xs flex items-center gap-1">
            <User className="w-3.5 h-3.5 text-gray-400" />
            {rec.customer_name || "Walk-in Customer"}
          </span>
          {rec.table_name && (
            <span className="text-[10px] text-gray-400 flex items-center gap-1">
              <MapPin className="w-3 h-3 text-gray-400" />
              Table: {rec.table_name}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Totals",
      key: "totals",
      render: (_: any, rec: Order) => (
        <div className="flex flex-col">
          <span className="text-[11px] text-gray-400">
            Subtotal: LKR{" "}
            {rec.subtotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </span>
          {rec.discount_amount > 0 && (
            <span className="text-[10px] text-red-500">
              Disc: -LKR{" "}
              {rec.discount_amount.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </span>
          )}
          {rec.other_charges > 0 && (
            <span className="text-[10px] text-green-600">
              Other: +LKR{" "}
              {rec.other_charges.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Grand Total",
      dataIndex: "grand_total",
      key: "grand_total",
      render: (v: number) => (
        <span className="font-bold text-indigo-600 text-xs">
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Details",
      key: "payments",
      render: (_: any, rec: Order) => (
        <div className="flex flex-col">
          <span className="text-xs text-green-600 font-medium">
            Paid: LKR{" "}
            {rec.paid_amount.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </span>
          {rec.due_amount > 0 ? (
            <span className="text-[10px] text-red-500 font-bold">
              Due: LKR{" "}
              {rec.due_amount.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </span>
          ) : (
            <span className="text-[10px] text-gray-300">No Due</span>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "payment_status_name",
      key: "payment_status_name",
      render: (s: string) => {
        let color = "error";
        if (s?.toLowerCase() === "paid") color = "success";
        else if (
          s?.toLowerCase() === "partially paid" ||
          s?.toLowerCase() === "partial"
        )
          color = "warning";
        return (
          <Tag color={color} className="text-[10px] uppercase font-bold">
            {s || "Unknown"}
          </Tag>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "order_status_name",
      key: "order_status_name",
      render: (s: string) => {
        let color = "blue";
        if (s?.toLowerCase() === "served" || s?.toLowerCase() === "ready")
          color = "success";
        else if (s?.toLowerCase() === "cancelled") color = "error";
        else if (s?.toLowerCase() === "on hold") color = "warning";
        return (
          <Tag color={color} className="text-[10px] uppercase font-bold">
            {s || "Unknown"}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 70,
      render: (_: any, rec: Order) => {
        const isCancelled =
          rec.order_status_name?.toLowerCase() === "cancelled";
        const hasDue = rec.due_amount > 0;
        const hasCustomer = !!rec.customer_id;
        const menuItems = [
          {
            key: "view",
            label: "View Order Details",
            icon: <EyeOutlined />,
            onClick: () => handleOpenDetails(rec.id),
          },
          {
            key: "print",
            label: "Print / Download Invoice",
            icon: <PrinterOutlined />,
            onClick: () => {
              setSelectedOrderId(rec.id);
              setInvoiceModalOpen(true);
            },
          },
          {
            key: "history",
            label: "View Edit History",
            icon: <HistoryOutlined />,
            onClick: () => handleOpenHistoryDirect(rec.id),
          },
          ...(!isCancelled && hasDue && hasCustomer
            ? [
                {
                  key: "credit_payment",
                  label: "Credit Payment",
                  icon: <DollarOutlined className="text-emerald-600" />,
                  onClick: () => handleOpenCreditPayment(rec),
                },
              ]
            : []),
          ...(!isCancelled && isAllowed(SalesOrderAction.Edit)
            ? [
                {
                  key: "edit",
                  label: "Edit / Load to Cart",
                  icon: <EditOutlined />,
                  onClick: () => handleEditOrder(rec.id),
                },
              ]
            : []),
          ...(!isCancelled && isAllowed(SalesOrderAction.Delete)
            ? [
                {
                  key: "delete",
                  label: "Delete / Cancel Order",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: "Cancel and Delete Order?",
                      content:
                        "Are you sure you want to permanently cancel and delete this order? This will reverse any stock deductions and ledger credits.",
                      okText: "Delete",
                      okButtonProps: { danger: true },
                      cancelText: "Cancel",
                      onOk: async () => {
                        try {
                          const res = await deleteMutation.mutateAsync(rec.id);
                          if (res?.success) {
                            message.success(
                              "Order deleted and stock/ledger changes reversed successfully.",
                            );
                          } else {
                            message.error(
                              res?.message || "Failed to delete order.",
                            );
                          }
                        } catch (err: any) {
                          message.error(
                            err?.message || "Failed to delete order.",
                          );
                        }
                      },
                    });
                  },
                },
              ]
            : []),
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              className="text-gray-500 hover:text-gray-700"
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Selling History"
        subText={`Total sales records: ${totalOrders}`}
        handleButtons={[{ title: "", icon: ReloadOutlined, onClick: refetch }]}
        searchBar={
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <Select
              allowClear
              placeholder="Customer Filter"
              onChange={(v) => {
                setCustomerId(v);
                setPage(1);
              }}
              className="w-full sm:w-44"
              showSearch
              optionFilterProp="children"
            >
              {customers.map((c: Customer) => (
                <Option key={c.id} value={c.id}>
                  {c.name}
                </Option>
              ))}
            </Select>

            <Select
              allowClear
              placeholder="Payment Status"
              onChange={(v) => {
                setPaymentStatusId(v);
                setPage(1);
              }}
              className="w-full sm:w-40"
            >
              {paymentStatuses.map((s: any) => (
                <Option key={s.id} value={s.id}>
                  {s.name}
                </Option>
              ))}
            </Select>

            <Select
              allowClear
              placeholder="Order Status"
              onChange={(v) => {
                setOrderStatusId(v);
                setPage(1);
              }}
              className="w-full sm:w-40"
            >
              {orderStatuses.map((s: any) => (
                <Option key={s.id} value={s.id}>
                  {s.name}
                </Option>
              ))}
            </Select>

            <Select
              allowClear
              placeholder="Selling Type"
              onChange={(v) => {
                setSellingType(v);
                setPage(1);
              }}
              className="w-full sm:w-36"
            >
              <Option value="retail">Retail</Option>
              <Option value="wholesale">Wholesale</Option>
            </Select>

            <Input
              placeholder="Search Order No, Customer, Table…"
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(1);
              }}
              allowClear
              onClear={() => {
                setSearchInput("");
                setPage(1);
              }}
              className="w-full sm:w-60"
            />
          </div>
        }
      />

      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <Table
          dataSource={ordersList?.data || []}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={tablePagination({
            current: page,
            pageSize: PAGE_SIZE,
            total: totalOrders,
            onChange: setPage,
            itemName: "sales",
          })}
          scroll={{ x: 1100 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No sales logged yet."
              />
            ),
          }}
        />
      </div>

      <OrderDetailsDrawer
        open={detailsDrawerOpen}
        onClose={handleCloseDetails}
        orderId={selectedOrderId}
        onViewHistory={handleOpenHistory}
      />

      <OrderHistoryDrawer
        open={historyDrawerOpen}
        onClose={handleCloseHistory}
        orderId={selectedOrderId}
      />

      <InvoiceModal
        open={invoiceModalOpen}
        orderId={selectedOrderId}
        onClose={() => {
          setInvoiceModalOpen(false);
          setSelectedOrderId(null);
        }}
      />

      <CreditPaymentDrawer
        open={isCreditPaymentDrawerOpen}
        onClose={() => {
          setIsCreditPaymentDrawerOpen(false);
          setCreditPaymentOrder(null);
        }}
        creditPaymentOrder={creditPaymentOrder}
        paymentMethods={paymentMethods}
        bankAccounts={bankAccounts}
        createRepaymentMutation={createRepaymentMutation}
        handleSaveCreditPayment={handleSaveCreditPayment}
      />
    </div>
  );
};
